import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useSession } from "./auth-client";
import { Sentry } from "./sentry";

export type AdminProfile = {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  phoneNumber: string | null;
  phoneNumberVerified: boolean;
  emailVerified: boolean;
  image: string | null;
  isSuperAdmin: boolean;
  superAdminRole: string | null;
};

type AdminAuthContextValue = {
  sessionData: ReturnType<typeof useSession>["data"];
  isPending: boolean;
  bootstrapLoading: boolean;
  bootstrapComplete: boolean;
  profile: AdminProfile | null;
  isAdmin: boolean;
  refresh: () => Promise<void>;
};

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const { data: sessionData, isPending } = useSession();
  const [profile, setProfile] = useState<AdminProfile | null>(null);
  const [bootstrapLoading, setBootstrapLoading] = useState(false);
  const [bootstrapComplete, setBootstrapComplete] = useState(false);

  const refresh = useCallback(async () => {
    if (!sessionData?.user) {
      setProfile(null);
      setBootstrapComplete(false);
      return;
    }

    setBootstrapLoading(true);
    setBootstrapComplete(false);

    const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
    const fetchProfile = () =>
      fetch(`${api}/api/users/profile`, { credentials: "include" });

    try {
      let profileRes: Response;
      try {
        profileRes = await fetchProfile();
      } catch {
        // Transient network failure during auth bootstrap — retry once.
        await new Promise((resolve) => setTimeout(resolve, 500));
        profileRes = await fetchProfile();
      }

      if (profileRes.ok) {
        setProfile((await profileRes.json()) as AdminProfile);
      } else {
        setProfile(null);
      }
    } catch (error) {
      // Network still unreachable after a retry. Report as handled so it
      // doesn't surface as an unhandled rejection.
      Sentry.captureException(error, {
        tags: { area: "admin-auth-bootstrap" },
      });
    } finally {
      setBootstrapLoading(false);
      setBootstrapComplete(true);
    }
  }, [sessionData?.user]);

  useEffect(() => {
    if (sessionData?.user) {
      void refresh();
    } else {
      setProfile(null);
      setBootstrapComplete(false);
    }
  }, [refresh, sessionData?.user]);

  const isAdmin = profile?.isSuperAdmin === true;

  const value = useMemo<AdminAuthContextValue>(
    () => ({
      sessionData,
      isPending,
      bootstrapLoading,
      bootstrapComplete,
      profile,
      isAdmin,
      refresh,
    }),
    [bootstrapComplete, bootstrapLoading, isPending, isAdmin, profile, refresh, sessionData],
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);

  if (!context) {
    throw new Error("useAdminAuth must be used within AdminAuthProvider");
  }

  return context;
}
